import React from 'react';
import styles from './Recording.module.css';

/** Category title — 20px semibold */
export const CategoryTitle = (props: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2 {...props} className={[styles.categoryTitle, props.className].filter(Boolean).join(' ')} />
);

/** Subtitle description text */
export const SubsectionCaption = (props: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p {...props} className={[styles.subsectionCaption, props.className].filter(Boolean).join(' ')} />
);

/** Popup overlay */
export const PopupOverlay = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.popupOverlay, props.className].filter(Boolean).join(' ')} />
);

/** Popup container */
export const PopupContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.popupContainer, props.className].filter(Boolean).join(' ')} />
);

/** Popup header */
export const PopupHeader = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.popupHeader, props.className].filter(Boolean).join(' ')} />
);

/** Popup title */
export const PopupTitle = (props: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 {...props} className={[styles.popupTitle, props.className].filter(Boolean).join(' ')} />
);

/** Time row (begin/end) */
export const TimeRow = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.timeRow, props.className].filter(Boolean).join(' ')} />
);

/** Time field group (label + selectors) */
export const TimeFieldGroup = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.timeFieldGroup, props.className].filter(Boolean).join(' ')} />
);

/** Time field label */
export const TimeFieldLabel = (props: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label {...props} className={[styles.timeFieldLabel, props.className].filter(Boolean).join(' ')} />
);

/** All day row */
export const AllDayRow = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.allDayRow, props.className].filter(Boolean).join(' ')} />
);

/** All day label */
export const AllDayLabel = (props: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label {...props} className={[styles.allDayLabel, props.className].filter(Boolean).join(' ')} />
);

/** Mode field (label + dropdown) */
export const ModeField = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.modeField, props.className].filter(Boolean).join(' ')} />
);

/** Mode field label */
export const ModeLabel = (props: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label {...props} className={[styles.modeLabel, props.className].filter(Boolean).join(' ')} />
);

/** Days selector section */
export const DaysSection = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.daysSection, props.className].filter(Boolean).join(' ')} />
);

/** Days selector label */
export const DaysSectionLabel = (props: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label {...props} className={[styles.daysSectionLabel, props.className].filter(Boolean).join(' ')} />
);

/** Days toggle row */
export const DaysToggleRow = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.daysToggleRow, props.className].filter(Boolean).join(' ')} />
);

interface DayToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $active: boolean;
  $isSource?: boolean;
  $isDisabled?: boolean;
}

/** Day toggle button */
export const DayToggle = ({ $active, $isSource, $isDisabled, className, ...rest }: DayToggleProps) => (
  <button
    {...rest}
    className={[
      styles.dayToggle,
      $active && styles.active,
      $isSource && styles.source,
      $isDisabled && styles.disabled,
      className,
    ].filter(Boolean).join(' ')}
  />
);

/** Popup footer (cancel/save) */
export const PopupFooter = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.popupFooter, props.className].filter(Boolean).join(' ')} />
);

/* ============================================
 * SCHEDULE EDITOR — full-screen calendar view
 * ============================================ */

/** Schedule editor top-level container */
export const ScheduleEditorContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.scheduleEditorContainer, props.className].filter(Boolean).join(' ')} />
);

/** Back button + camera name row + action buttons */
export const ScheduleEditorHeader = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.scheduleEditorHeader, props.className].filter(Boolean).join(' ')} />
);

/** Spacer to push action buttons to the right */
export const HeaderSpacer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.headerSpacer, props.className].filter(Boolean).join(' ')} />
);

/** Header action buttons container (Cancel + Save) */
export const HeaderActions = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.headerActions, props.className].filter(Boolean).join(' ')} />
);

/** Camera name title in schedule editor */
export const CameraNameTitle = (props: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h1 {...props} className={[styles.cameraNameTitle, props.className].filter(Boolean).join(' ')} />
);

/** Legend row with color swatches + action buttons */
export const LegendRow = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.legendRow, props.className].filter(Boolean).join(' ')} />
);

/** Single legend item (swatch + label) */
export const LegendItem = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.legendItem, props.className].filter(Boolean).join(' ')} />
);

/** Legend color swatch — "Camera Default" */
export const LegendSwatchDefault = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.legendSwatchDefault, props.className].filter(Boolean).join(' ')} />
);

/** Legend color swatch — "Scheduled Override" */
export const LegendSwatchOverride = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.legendSwatchOverride, props.className].filter(Boolean).join(' ')} />
);

/** Legend label text */
export const LegendLabel = (props: React.HTMLAttributes<HTMLSpanElement>) => (
  <span {...props} className={[styles.legendLabel, props.className].filter(Boolean).join(' ')} />
);

/** Scheduler wrapper to contain the Blueprint Scheduler */
export const SchedulerWrapper = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={[styles.schedulerWrapper, props.className].filter(Boolean).join(' ')} />
);
